import React, { Component } from 'react';
import axios from 'axios';
import ReactLoading from 'react-loading';
import { Element, scroller } from 'react-scroll';

// Styles
import './index.scss';
import './popover.scss';

import CategoryFilter from './category/CategoryFilter';
import LocationFilter from './location/LocationFilter';
import GuestFilter from './guest/GuestFilter';
import DateFilter from './date/DateFilter';
import SearchFilter from './search/SearchFilter';

import MobileFilters from './mobile_filters/MobileFilters';

import Paginator from './paginator/Paginator';

const mobileBreakpointWidth = 700;

class TourFilters extends Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: false,
      searchLoading: false,
      tours: this.props.tours,
      total_tours: this.props.total_tours,
      page: 1,
      randomizationSeed: this.props.randomization_seed,
      appliedFilters: [], // Holds the names + data of each applied filter
      dateFilterOpen: false,
      guestFilterOpen: false,
      categoryFilterOpen: false,
      countryFilterOpen: false,
      cityFilterOpen: false,
    };
  }

  // Required to rerender when mobile width detected =====
  componentDidMount() {
    window.addEventListener('resize', this.resize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.resize);
  }

  resize = () => this.forceUpdate();
  // =====

  // Applies a single filter
  applyFilter = (name = null, data = null, page = null) => {
    // Prepare payload
    const params = {
      filterApplied: true,
      filters: {},
    };

    // Load previously applied filters
    const { appliedFilters } = this.state;

    appliedFilters.forEach((filter) => {
      params.filters[filter.name] = filter.data;
    });

    if (name === 'search' || (name && data)) {
      // Append newly applied filter
      params.filters[name] = data;

      // Update state
      const appliedFilterIndex = appliedFilters.findIndex(
        (filter) => filter.name === name
      );
      if (appliedFilterIndex === -1) {
        appliedFilters.push({ name, data });
      } else {
        appliedFilters[appliedFilterIndex] = { name, data };
      }
    }

    this.setState({
      appliedFilters,
      loading: true,
      searchLoading: name === 'search',
    });

    // Update page if necessary
    if (page) params.page = page;

    // Send request
    this.sendRequest(params);
  };

  // Apply an array of filters (used by MobileFilters)
  applyMultipleFilters = (filters, page = null) => {
    // Prepare payload
    const params = {
      filterApplied: true,
      filters: {},
    };

    // Load previously applied filters
    const { appliedFilters } = this.state;

    appliedFilters.forEach((filter) => {
      params.filters[filter.name] = filter.data;
    });

    filters.forEach((f) => {
      if (f.name === 'search' || (f.name && f.data)) {
        // Append newly applied filter
        params.filters[f.name] = f.data;

        // Update state
        const appliedFilterIndex = appliedFilters.findIndex(
          (filter) => filter.name === f.name
        );
        if (appliedFilterIndex === -1) {
          appliedFilters.push({ name: f.name, data: f.data });
        } else {
          appliedFilters[appliedFilterIndex] = { name: f.name, data: f.data };
        }
      }
    });

    this.setState({
      appliedFilters,
      loading: true,
    });

    // Update page if necessary
    if (page) params.page = page;

    // Send request
    this.sendRequest(params);
  };

  clearAllAppliedFilters = () => {
    this.setState({ loading: true, appliedFilters: [] });
    const params = {
      filterApplied: true,
      filters: {},
    };
    this.sendRequest(params);
  };

  sendRequest = (params) => {
    const { randomizationSeed } = this.state;
    params.randomization_seed = randomizationSeed;

    axios
      .get(
        `/i/guide-association/${this.props.slug}/tours`,
        {
          params,
        },
        {
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        }
      )
      .then((response) => {
        this.setState({
          tours: response.data.tours,
          total_tours: response.data.total_tours,
          loading: false,
          searchLoading: false,
        });
      })
      .catch((_error) => {
        this.setState({ loading: false, searchLoading: false });
      });
  };

  // Closes all filter popovers
  closeAllFilters = () => {
    this.setState({
      dateFilterOpen: false,
      guestFilterOpen: false,
      categoryFilterOpen: false,
      countryFilterOpen: false,
      cityFilterOpen: false,
    });
  };

  // Closes all filter popovers and opens the selected one
  openFilter = (filter) => {
    this.closeAllFilters();
    if (filter === 'date') this.setState({ dateFilterOpen: true });
    if (filter === 'guest') this.setState({ guestFilterOpen: true });
    if (filter === 'category') this.setState({ categoryFilterOpen: true });
    if (filter === 'country') this.setState({ countryFilterOpen: true });
    if (filter === 'city') this.setState({ cityFilterOpen: true });
  };

  closeFilter = (filter) => {
    if (filter === 'date') this.setState({ dateFilterOpen: false });
    if (filter === 'guest') this.setState({ guestFilterOpen: false });
    if (filter === 'category') this.setState({ categoryFilterOpen: false });
    if (filter === 'country') this.setState({ countryFilterOpen: false });
    if (filter === 'city') this.setState({ cityFilterOpen: false });
  };

  // Returns whether any non-search filters are open
  isFilterOpen = () => {
    const {
      dateFilterOpen,
      guestFilterOpen,
      categoryFilterOpen,
      cityFilterOpen,
      countryFilterOpen,
    } = this.state;
    return (
      dateFilterOpen ||
      guestFilterOpen ||
      categoryFilterOpen ||
      cityFilterOpen ||
      countryFilterOpen
    );
  };

  // Returns whether any non-search filters have been applied
  anyFiltersApplied = () => {
    let applied = false;

    this.state.appliedFilters.forEach((filter) => {
      if (filter.name === 'date' && Object.keys(filter.data).length > 0) {
        applied = true;
      } else if (filter.name === 'guest' && filter.data > 0) {
        applied = true;
      } else if (
        (filter.name === 'category' ||
          filter.name === 'country' ||
          filter.name === 'city') &&
        filter.data.length > 0
      ) {
        applied = true;
      }
    });

    return applied;
  };

  paginateTo = (page) => {
    this.applyFilter(null, null, page);
    this.setState({ page });
    scroller.scrollTo('filterScrollElement', {
      smooth: true,
      delay: 0,
    });
  };

  commonFilterProps = () => {
    return {
      openFilter: this.openFilter,
      closeFilter: this.closeFilter,
      applyFilter: this.applyFilter,
      loading: this.state.loading,
    };
  };

  dateFilterProps = () => {
    const dateFilter = this.state.appliedFilters.find(
      (filter) => filter.name === 'date'
    );
    const filterApplied =
      dateFilter &&
      dateFilter.data &&
      (dateFilter.data.start || dateFilter.data.end);

    let appliedDates = null;
    if (dateFilter && dateFilter.data) {
      appliedDates = { ...dateFilter.data };
    }

    return {
      enabled: this.props.filters.settings.availability_by_date,
      open: this.state.dateFilterOpen,
      calendarStartsMonday: this.props.calendar_starts_monday,
      filterApplied,
      appliedDates,
    };
  };

  guestFilterProps = () => {
    const guestFilter = this.state.appliedFilters.find(
      (filter) => filter.name === 'guest'
    );
    const filterApplied =
      guestFilter && guestFilter.data && guestFilter.data > 1;

    let appliedGuestCount = null;
    if (guestFilter && guestFilter.data) {
      appliedGuestCount = guestFilter.data;
    }

    return {
      enabled: this.props.filters.settings.availability_by_guests,
      eventTypes: 'mouseup',
      open: this.state.guestFilterOpen,
      filterApplied,
      appliedGuestCount,
    };
  };

  categoryFilterProps = () => {
    const catFilter = this.state.appliedFilters.find(
      (filter) => filter.name === 'category'
    );
    const filterApplied =
      catFilter && catFilter.data && catFilter.data.length > 0;

    let appliedCategories = null;
    if (catFilter && catFilter.data) {
      appliedCategories = catFilter.data.slice();
    }

    const { categories, custom_categories } = this.props.filters.settings;
    return {
      enabled: categories || custom_categories,
      eventTypes: 'mouseup',
      open: this.state.categoryFilterOpen,
      filterApplied,
      appliedCategories,
      categories: this.props.filters.categories,
    };
  };

  countryFilterProps = () => {
    const countryFilter = this.state.appliedFilters.find(
      (filter) => filter.name === 'country'
    );
    const filterApplied =
      countryFilter && countryFilter.data && countryFilter.data.length > 0;

    let appliedCountries = null;
    if (countryFilter && countryFilter.data) {
      appliedCountries = countryFilter.data.slice();
    }

    return {
      enabled: this.props.filters.settings.destinations,
      eventTypes: 'mouseup',
      open: this.state.countryFilterOpen,
      locationType: 'country',
      filterApplied,
      appliedLocations: appliedCountries,
      locations: this.props.filters.countries,
    };
  };

  cityFilterProps = () => {
    const cityFilter = this.state.appliedFilters.find(
      (filter) => filter.name === 'city'
    );
    const filterApplied =
      cityFilter && cityFilter.data && cityFilter.data.length > 0;

    let appliedCities = null;
    if (cityFilter && cityFilter.data) {
      appliedCities = cityFilter.data.slice();
    }

    return {
      enabled: this.props.filters.settings.destination_cities,
      eventTypes: 'mouseup',
      open: this.state.cityFilterOpen,
      locationType: 'city',
      filterApplied,
      appliedLocations: appliedCities,
      locations: this.props.filters.cities,
    };
  };

  // Need to preserve existing backend code so going with this for now
  // TODO: Refactor to use real components
  renderToursList = () => {
    const mobile = window.innerWidth < mobileBreakpointWidth;
    if (mobile && this.state.loading && !this.state.searchLoading) {
      return (
        <ReactLoading
          className="loading"
          type="spin"
          color="#8ec63f"
          height="60px"
          width="60px"
          delay="0"
        />
      );
    }

    return (
      <div>
        {this.state.tours.map((tour) => {
          const html = { __html: tour };
          return <div dangerouslySetInnerHTML={html} />;
        })}
      </div>
    );
  };

  renderFilters = () => {
    // Render mobile filters
    if (window.innerWidth < mobileBreakpointWidth) {
      return <div className="filters">{this.renderMobileFilters()}</div>;
    }

    // Render desktop filters
    return (
      <div className="filters">
        {this.renderDateFilter()}
        {this.renderGuestFilter()}
        {this.renderCategoryFilter()}
        {this.renderCountryFilter()}
        {this.renderCityFilter()}
      </div>
    );
  };

  renderDateFilter = () => {
    if (!this.props.filters.settings.availability_by_date) return null;
    return (
      <DateFilter {...this.commonFilterProps()} {...this.dateFilterProps()} />
    );
  };

  renderGuestFilter = () => {
    if (!this.props.filters.settings.availability_by_guests) return null;
    return (
      <GuestFilter {...this.commonFilterProps()} {...this.guestFilterProps()} />
    );
  };

  renderCategoryFilter = () => {
    const { categories, custom_categories } = this.props.filters.settings;
    if (!(categories || custom_categories)) return null;
    return (
      <CategoryFilter
        {...this.commonFilterProps()}
        {...this.categoryFilterProps()}
      />
    );
  };

  renderCountryFilter = () => {
    if (!this.props.filters.settings.destinations) return null;
    return (
      <LocationFilter
        {...this.commonFilterProps()}
        {...this.countryFilterProps()}
      />
    );
  };

  renderCityFilter = () => {
    if (!this.props.filters.settings.destination_cities) return null;
    return (
      <LocationFilter
        {...this.commonFilterProps()}
        {...this.cityFilterProps()}
      />
    );
  };

  renderSearchFilter = () => {
    return (
      <SearchFilter
        applyFilter={this.applyFilter}
        loading={this.state.searchLoading}
      />
    );
  };

  renderMobileFilters = () => {
    const dateFilterProps = this.dateFilterProps();
    const guestFilterProps = this.guestFilterProps();
    const categoryFilterProps = this.categoryFilterProps();
    const countryFilterProps = this.countryFilterProps();
    const cityFilterProps = this.cityFilterProps();

    // There should be atleast one filter enabled to render
    const anyFiltersEnabled =
      dateFilterProps.enabled ||
      guestFilterProps.enbabled ||
      categoryFilterProps.enabled ||
      countryFilterProps.enabled ||
      cityFilterProps.enabled;
    if (!anyFiltersEnabled) return null;

    return (
      <MobileFilters
        commonFilterProps={this.commonFilterProps()}
        dateFilterProps={dateFilterProps}
        guestFilterProps={guestFilterProps}
        categoryFilterProps={categoryFilterProps}
        countryFilterProps={countryFilterProps}
        cityFilterProps={cityFilterProps}
        applyMultipleFilters={this.applyMultipleFilters}
        clearAllAppliedFilters={this.clearAllAppliedFilters}
        anyFiltersApplied={this.anyFiltersApplied}
      />
    );
  };

  renderPaginator = (id) => {
    if (id === 2 && (this.state.loading || this.state.searchLoading))
      return null;

    return (
      <Paginator
        currentPage={this.state.page}
        totalPages={Math.ceil(this.state.total_tours / 10)}
        paginate={this.paginateTo}
        forcePage={this.state.page - 1}
      />
    );
  };

  renderErrorMessage = () => {
    if (this.state.loading || this.state.tours.length > 0) return null;

    let searchMsg = null;
    let filterMsg = null;

    const searchFilter = this.state.appliedFilters.find(
      (filter) => filter.name === 'search'
    );
    if (searchFilter && searchFilter.data.length > 0) {
      searchMsg = (
        <p>
          Sorry, we found no results for
          <span className="searchterm">{` "${searchFilter.data}"`}</span>.
        </p>
      );
    }

    // Check if atleast one non-search filter applied
    if (this.anyFiltersApplied()) {
      if (searchMsg) {
        filterMsg = <p>Try widening your search by changing your filters.</p>;
      } else {
        searchMsg = <p>Sorry, we found no results for the selected filters.</p>;
        filterMsg = (
          <p>Try widening your search by changing or clearing your filters.</p>
        );
      }
    }

    return (
      <div className="tours-error">
        {searchMsg}
        {filterMsg}
      </div>
    );
  };

  render() {
    return (
      <div>
        <Element name="filterScrollElement">
          <div className="tourFilters">
            {this.renderFilters()}
            {this.renderSearchFilter()}
          </div>
        </Element>
        <div
          className={`tours clearfix ${
            this.state.loading || this.isFilterOpen() ? 'faded' : null
          }`}
          id="tours_list"
        >
          <div className="website-tours-wrapper" id="tours-anchor">
            {this.renderPaginator()}
            <div className="website-tours">{this.renderToursList()}</div>
            {this.renderPaginator(2)}
            {this.renderErrorMessage()}
          </div>
        </div>
      </div>
    );
  }
}

export default TourFilters;
